import { actions } from "store/auth";

import eventsService, { EVENTS } from "services/events";
import {
  authenticateThunk,
  changePasswordThunk,
  fetchUserThunk,
  forgotPasswordThunk,
  getCreditsThunk,
  groupsListThunk,
  loginThunk,
  logoutThunk,
  oauth2GetAuthUriThunk,
  oauth2LoginThunk,
  ownersListThunk,
  resendVerifyEmailThunk,
  searchUsersAndGroupsThunk,
  sendFeedbackThunk,
  sendPromoCodeThunk,
  signupThunk,
  updateTLevelThunk,
  updateUserDomainInfoThunk,
  updateLocalStorageThunk,
  updateDemoWizardThunk,
  usersListThunk,
  verifyEmailThunk,
  deleteAccountThunk,
  updateSettingsThunk,
  getApiKeyThunk,
  createApiKeyThunk,
  deleteApiKeyThunk,
  sendGoogleEventThunk,
  npsSurveyThunk,
} from "thunks/auth";
import { logger } from "utils/logger";
import {
  CSRF_TOKEN_KEY,
  DOWNLOAD_MESSAGE_SHOW_TIME_KEY,
  JIRA_ISSUE_CONNECTOR_EMAIL_KEY,
  JIRA_ISSUE_CONNECTOR_FIRST_LAST_NAME_KEY,
  LINKS,
  LOCALSTORAGE_ITEMS,
  SORT_DIRECTION,
  VOICE_VISIBILITY,
} from "../constants";
import { initAppData } from "./initialization";
import { notificationDisplay } from "./notification";
import { voicesList } from "./voice";
import localStorageService from "services/localStorage";

export const { userUpdate, toggleGlobalLoading, updateCredits } = actions;

export const authenticate = () => async (dispatch) => {
  try {
    const data = await dispatch(authenticateThunk()).unwrap();
    dispatch(initAppData(data));
  } catch (error) {
    logger.trace(error);
  }
};

export const login = (payload) => async (dispatch) => {
  try {
    const data = await dispatch(loginThunk(payload)).unwrap();
    const { user, csrf_token } = data;
    localStorage.setItem(CSRF_TOKEN_KEY, csrf_token);
    document.location = LINKS.loginRedirect;
    /*
    dispatch(initAppData(user));
    await dispatch(
      voicesList({
        offset: 0,
        limit: 1,
        sortKey: "rating",
        sortDirection: SORT_DIRECTION.desc,
        filters: { type: [VOICE_VISIBILITY.private] },
        privateVoicesCheck: true,
      })
    );
    */
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const oauth2GetAuthUri = (payload) => async (dispatch) => {
  try {
    const { auth_uri } = await dispatch(
      oauth2GetAuthUriThunk(payload)
    ).unwrap();
    return auth_uri;
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const oauth2Login = (payload) => async (dispatch) => {
  try {
    const { user, csrf_token } = await dispatch(
      oauth2LoginThunk(payload)
    ).unwrap();
    localStorage.setItem(CSRF_TOKEN_KEY, csrf_token);
    document.location = LINKS.loginRedirect;
    /*
    await dispatch(
      voicesList({
        offset: 0,
        limit: 1,
        sortKey: "rating",
        sortDirection: SORT_DIRECTION.desc,
        filters: { type: [VOICE_VISIBILITY.private] },
        privateVoicesCheck: true,
      })
    );
    dispatch(initAppData(user));
    */
  } catch (error) {
    dispatch(notificationDisplay("error", error.detail));
    logger.trace(error);
  }
};

export const logout = () => async (dispatch) => {
  try {
    await dispatch(logoutThunk());
    localStorage.removeItem(JIRA_ISSUE_CONNECTOR_EMAIL_KEY);
    localStorage.removeItem(JIRA_ISSUE_CONNECTOR_FIRST_LAST_NAME_KEY);
    localStorage.removeItem(CSRF_TOKEN_KEY);
    localStorage.removeItem(DOWNLOAD_MESSAGE_SHOW_TIME_KEY);
    localStorageService.setItem(LOCALSTORAGE_ITEMS.lastUsedVoices, "");
    // fyi test for fixing old landing cache problem
    setTimeout(() => {
      document.location = "/";
    }, 2000);
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const signup = (payload) => async (dispatch) => {
  try {
    const data = await dispatch(signupThunk(payload)).unwrap();
    const { user, csrf_token, promo_status } = data;
    localStorage.setItem(CSRF_TOKEN_KEY, csrf_token);
    dispatch(initAppData(user));
    if (promo_status !== null) {
      if (promo_status) {
        dispatch(
          notificationDisplay("success", "Promo code applied successfully.")
        );
      } else {
        dispatch(notificationDisplay("error", "Invalid promo code."));
      }
      localStorageService.clearItem(LOCALSTORAGE_ITEMS.promo);
    }
    dispatch(
      notificationDisplay(
        "success",
        "Thanks for signing up. Please check your mailbox for further instructions."
      )
    );
    window.gtag &&
      window.gtag("event", "conversion", {
        send_to: `${process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID}/o7YpCIig-uIBEMyC2YEC`,
      });
    return null;
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const verifyEmail = (payload) => async (dispatch) => {
  try {
    await dispatch(verifyEmailThunk(payload));
    dispatch(
      notificationDisplay(
        "success",
        "Thanks. Your email was verified successfully."
      )
    );
    return true;
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const resendVerifyEmail = () => async (dispatch) => {
  try {
    await dispatch(resendVerifyEmailThunk());
    dispatch(notificationDisplay("success", "Email sent successfully"));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const forgotPassword = (payload) => async (dispatch) => {
  try {
    const data = await dispatch(forgotPasswordThunk(payload));
    dispatch(notificationDisplay("success", "Email sent successfully"));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const changePassword = (payload) => async (dispatch) => {
  try {
    await dispatch(changePasswordThunk(payload)).unwrap();
    dispatch(
      notificationDisplay(
        "success",
        "Your password has been successfully updated."
      )
    );
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const usersList = (payload) => async (dispatch) => {
  try {
    await dispatch(usersListThunk(payload));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const groupsList = (payload) => async (dispatch) => {
  try {
    await dispatch(groupsListThunk(payload));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const searchUsersAndGroups = (payload) => async (dispatch) => {
  try {
    await dispatch(searchUsersAndGroupsThunk(payload));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const ownersList = (payload) => async (dispatch) => {
  try {
    await dispatch(ownersListThunk(payload));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const getCredits = () => async (dispatch) => {
  try {
    await dispatch(getCreditsThunk());
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const sendPromoCode = (payload) => async (dispatch) => {
  try {
    const user = await dispatch(sendPromoCodeThunk(payload)).unwrap();
    dispatch(initAppData(user));
    dispatch(
      notificationDisplay("success", "Promo code applied successfully!")
    );
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const updateTlevel = (payload) => async (dispatch) => {
  try {
    await dispatch(updateTLevelThunk(payload)).unwrap();
    dispatch(notificationDisplay("success", "T-Level updated!"));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const updateUserDomainInfo = (payload) => async (dispatch) => {
  try {
    await dispatch(updateUserDomainInfoThunk(payload));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const fetchUser = (payload) => async (dispatch) => {
  try {
    await dispatch(fetchUserThunk(payload)).unwrap();
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const sendFeedback = (payload) => async (dispatch) => {
  try {
    await dispatch(sendFeedbackThunk(payload)).unwrap();

    const { type, topic } = payload;
    eventsService.track(EVENTS.SUBMIT_FEEDBACK, { type, topic });
    dispatch(notificationDisplay("success", "Feedback submitted successfully"));
  } catch (error) {
    dispatch(
      notificationDisplay(
        "error",
        "Something went wrong while trying to update feedback"
      )
    );
    logger.trace(error);
  }
};

export const sendGoogleEvent = (payload) => async (dispatch) => {
  try {
    await dispatch(sendGoogleEventThunk(payload)).unwrap();
  } catch (error) {
    logger.trace(error);
  }
};

export const updateLocalStorage = (payload) => async (dispatch) => {
  try {
    await dispatch(updateLocalStorageThunk(payload));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const updateDemoWizard = (payload) => async (dispatch) => {
  try {
    await dispatch(updateDemoWizardThunk(payload));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const deleteAccount = () => async (dispatch) => {
  try {
    await dispatch(deleteAccountThunk());
    localStorage.removeItem(JIRA_ISSUE_CONNECTOR_EMAIL_KEY);
    localStorage.removeItem(JIRA_ISSUE_CONNECTOR_FIRST_LAST_NAME_KEY);
    localStorage.removeItem(CSRF_TOKEN_KEY);
    localStorage.removeItem(DOWNLOAD_MESSAGE_SHOW_TIME_KEY);
    document.location = "/";
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const updateSettings = (payload) => async (dispatch) => {
  try {
    await dispatch(updateSettingsThunk(payload));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const getApiKey = (payload) => async (dispatch) => {
  try {
    await dispatch(getApiKeyThunk(payload));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const createApiKey = (payload) => async (dispatch) => {
  try {
    await dispatch(createApiKeyThunk(payload));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const deleteApiKey = (payload) => async (dispatch) => {
  try {
    await dispatch(deleteApiKeyThunk(payload));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};

export const npsSurvey = (payload) => async (dispatch) => {
  try {
    await dispatch(npsSurveyThunk(payload));
    dispatch(notificationDisplay("success", "Feedback submitted successfully"));
  } catch (error) {
    dispatch(notificationDisplay("error", error));
    logger.trace(error);
  }
};
